import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PropertyCardPremium from "@/components/properties/PropertyCardPremium";
import FeaturedHeroCard from "@/components/properties/FeaturedHeroCard";
import { Skeleton } from "@/components/ui/skeleton";
import { propertyService } from "@/services";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from "react-i18next";

// Category tabs mapped to resilient propertyService.list() queries.
const CATEGORIES = [
  { key: "ai", label: "AI Recommended", params: { premium: true, sort: "popular", pageSize: 7 }, fallback: { sort: "newest", pageSize: 7 } },
  { key: "featured", label: "Featured", params: { featured: true, sort: "newest", pageSize: 6 }, fallback: { sort: "newest", pageSize: 6 } },
  { key: "luxury", label: "Luxury", params: { minPrice: 15000000, sort: "price_desc", pageSize: 6 } },
  { key: "affordable", label: "Affordable", params: { maxPrice: 8000000, sort: "price_asc", pageSize: 6 } },
];

export const FeaturedProperties = () => {
  const { t: ft } = useTranslation();
  const [active, setActive] = useState("ai");
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState(true);
  const { isDark } = useTheme();

  const fetchCategory = useCallback(async (key) => {
    const cat = CATEGORIES.find((c) => c.key === key);
    if (!cat) return [];
    let res = await propertyService.list(cat.params).catch(() => ({ data: [] }));
    if (!res?.data?.length && cat.fallback) {
      res = await propertyService.list(cat.fallback).catch(() => ({ data: [] }));
    }
    // Dedupe by id so a listing never renders twice (avoids duplicate React keys).
    const seen = new Set();
    return (res?.data || []).filter((p) => {
      if (!p || seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (cache[active] !== undefined) { setLoading(false); return undefined; }
    setLoading(true);
    (async () => {
      const data = await fetchCategory(active);
      if (!cancelled) {
        setCache((prev) => ({ ...prev, [active]: data }));
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active, cache, fetchCategory]);

  const items = cache[active] || [];
  const showHero = active === "ai" && items.length > 0;
  const gridItems = showHero ? items.slice(1) : items;

  return (
    <section className="ms-root bg-muted/40 py-12 sm:py-16" data-mapsearch-theme={isDark ? "dark" : "light"} data-testid="featured-section">
      <div className="abay-container">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#1E3A8A]/8 px-3 py-1 text-xs font-semibold text-[#1E3A8A] dark:bg-[#7AA0FF]/12 dark:text-[#7AA0FF]">
              <Sparkles className="h-3.5 w-3.5" /> Handpicked &amp; AI-ranked
            </span>
            <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">{ft("featured.title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground sm:text-base">Explore listings by what matters most to you.</p>
          </div>
          <Link to="/map-search" data-testid="view-all-properties" className="flex shrink-0 items-center gap-1 text-sm font-semibold text-[#1E3A8A] hover:underline dark:text-[#7AA0FF]">
            {ft("common.viewAll")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Category tabs */}
        <Tabs value={active} onValueChange={setActive} className="mt-5">
          <div className="-mx-4 overflow-x-auto px-4 thin-scroll sm:mx-0 sm:px-0">
            <TabsList data-testid="featured-category-tabs" className="h-auto w-max flex-nowrap gap-1 bg-muted/70 p-1">
              {CATEGORIES.map((c) => (
                <TabsTrigger
                  key={c.key}
                  value={c.key}
                  data-testid={`featured-tab-${c.key}`}
                  className="whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white data-[state=active]:shadow-[var(--shadow-sm)]"
                >
                  {c.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </Tabs>
      </div>

      {/* Content */}
      {loading ? (
        <div className="abay-container mt-6 space-y-4">
          {active === "ai" && <Skeleton className="h-56 w-full rounded-[var(--ms-radius-lg)]" />}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-80 w-full rounded-[var(--ms-radius-lg)]" />)}
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="abay-container mt-6">
          <div className="rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center" data-testid="featured-empty">
            <p className="font-semibold text-foreground">No properties in this category yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Try another category or explore the full map.</p>
            <Link to="/map-search" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A] hover:underline dark:text-[#7AA0FF]">
              Explore all listings <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      ) : (
        <div className="abay-container mt-6">
          {showHero && (
            <motion.div
              key={`hero-${items[0].id}`}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-5"
            >
              <FeaturedHeroCard property={items[0]} />
            </motion.div>
          )}

          {gridItems.length > 0 && (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="featured-grid">
              {gridItems.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.32, delay: Math.min(i, 5) * 0.05 }}
                >
                  <PropertyCardPremium property={p} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
